import { addSlideFooter } from '../slide_footer';
import { SURVEY_TITLE_OPTIONS, FOOTER_TEXT } from '../constants';
import { cleanQuestionTitle } from 'app/pages/Dashboard/pages/Survey/components/Dashboard/utilities/helpers.js';

/**
 * Creates Word Cloud slides using PNG snapshots from dashboard components.
 * Mirrors the title/footers used by chart slides and supports compare-by segmentation.
 *
 * Input shapes:
 * - overall mode: wordCloudRefs = { overall: React.RefObject<HTMLElement> }
 * - segmented mode: wordCloudRefs = { segments: Record<string, React.RefObject<HTMLElement>> }
 */
export const createWordCloudSlide = async ({
  compareBy,
  pptx,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  title,
  wordCloudImages,
}) => {
  if (!questionTitle || questionTitle.length < 3) return null;
  const cleanTitle = cleanQuestionTitle(questionTitle);
  if (!cleanTitle) return null;

  const questionPrefix = questionNumber ? `Q${questionNumber}. ` : '';
  const baseTitle = `${questionPrefix}${cleanTitle}`;

  const isOverall = compareBy?.value === 'overall' || compareBy === 'overall';

  if (isOverall) {
    const overallImage = wordCloudImages?.overall;
    if (!overallImage) return null;
    await createSingleWordCloudSlide({
      imageDataUrl: overallImage,
      pptx,
      reportType,
      reportUrl,
      slideTitle: baseTitle,
      title,
    });
    return;
  }

  const entries = Object.entries(wordCloudImages?.segments || {}).filter(([, img]) => !!img);
  if (!entries.length) return null;
  for (const [segmentName, segmentImage] of entries) {
    const slideTitle = `${baseTitle} Compared by: ${segmentName}`;
    await createSingleWordCloudSlide({
      imageDataUrl: segmentImage,
      pptx,
      reportType,
      reportUrl,
      slideTitle,
      title,
    });
  }
};

/**
 * Renders a single Word Cloud slide from a PNG data URL.
 */
const createSingleWordCloudSlide = async ({
  imageDataUrl,
  pptx,
  reportType,
  reportUrl,
  slideTitle,
  title,
}) => {
  const slide = pptx.addSlide();

  // Title styling mirrors survey chart slide
  const titleOptions = SURVEY_TITLE_OPTIONS;
  slide.addText(slideTitle, titleOptions);

  const chartYPosition = titleOptions.h > 1.0 ? 1.4 : 1.1;
  if (imageDataUrl) {
    slide.addImage({
      data: imageDataUrl,
      h: '62%',
      w: '75%',
      x: 1.0,
      y: chartYPosition,
    });
  } else {
    slide.addText('Word Cloud image could not be created', {
      h: '62%',
      w: '75%',
      x: 1.0,
      y: chartYPosition,
    });
  }

  addSlideFooter({ pptx, reportType, reportUrl, slide });

  // Add Source footer with hyperlink (consistent with chart slide)
  slide.addText(
    [{ text: 'Source: ' }, { options: { hyperlink: { url: reportUrl } }, text: title }],
    FOOTER_TEXT
  );

  return slide;
};
